'use client';

import { useEffect, useCallback } from 'react';
import { useStore } from '@/store/store';
import { getConfig } from '@/utils/config/config';
import { JackpotFeed } from '@fanduel/casino-swj-consumer';

import type { SiteWideJackpotData } from '@/types/aggregation/site-wide-jackpot';
import type { JackpotFeedResponse } from '@fanduel/casino-swj-consumer';
import type { UpdatedJackpotTier } from '@/types/zones/slices/site-wide-jackpot-slice';
import { buildApiUrl } from '@/utils/url/build-url';
import { getCookieValue } from '@/utils/cookies/client';
import { COOKIES } from '@/utils/cookies/constants';

const JUST_WON_ANIMATION_THRESHOLD = 10;

export const useJackpotFeed = (jackpotData: SiteWideJackpotData) => {
    const config = getConfig();

    const tiersCount = useStore((state) => state.siteWideJackpot.updatedTiers.length);
    const setUpdatedTiers = useStore((state) => state.siteWideJackpot.setUpdatedTiers);
    const setLoading = useStore((state) => state.siteWideJackpot.setLoading);
    const setActiveIndex = useStore((state) => state.siteWideJackpot.setActiveIndex);

    const { tiers, enabled } = jackpotData;
    const providerJackpotId = jackpotData?.provider_jackpot_id?.[0]?.text;

    const handleJackpotUpdate = useCallback(
        (jackpots: JackpotFeedResponse[]): UpdatedJackpotTier[] | undefined => {
            const storeUpdatedTiers = useStore.getState().siteWideJackpot.updatedTiers;
            const previousTiers = storeUpdatedTiers.length ? storeUpdatedTiers : tiers;

            // If no jackpots returned keep the last values.
            if (!jackpots?.length) {
                setLoading(false);
                return;
            }

            const fetchedJackpot = jackpots.find((jackpot) => jackpot.id === providerJackpotId);

            // If no jackpot found for the provided tiers or the jackpot found has not tiers keep the last values.
            if (!fetchedJackpot?.tiers?.length) {
                setLoading(false);
                return;
            }

            const newTiers: UpdatedJackpotTier[] = previousTiers
                .map((tier) => {
                    const fetchedTier = fetchedJackpot.tiers?.find((jackpot) => jackpot?.id === tier.tier_id);

                    if (!fetchedTier) {
                        return null;
                    }

                    const newTier: UpdatedJackpotTier = Object.assign({}, tier);
                    newTier.currentAmount = fetchedTier.currentAmount;
                    newTier.prevAmount = fetchedTier.prevAmount;
                    newTier.lastWonTime = fetchedTier.lastWonTime as string;

                    return newTier;
                })
                .filter((tier) => tier) as UpdatedJackpotTier[];

            if (!newTiers.length) {
                return;
            }

            setUpdatedTiers((prev: UpdatedJackpotTier[]) => {
                return newTiers.map((tier: UpdatedJackpotTier, index: number) => ({
                    ...tier,
                    // Don't take into consideration consecutive.
                    hasJustBeenWon:
                        !prev?.[index] || prev[index].hasJustBeenWon
                            ? false
                            : Number(tier.lastWonTime) - Number(prev[index].lastWonTime) > JUST_WON_ANIMATION_THRESHOLD
                }));
            });
            setLoading(false);
        },
        [providerJackpotId, setLoading, setUpdatedTiers]
    );

    const handleJackpotError = useCallback(
        (error: unknown) => {
            setLoading(false);
            console.log(error);
        },
        [setLoading]
    );

    useEffect(() => {
        // Only initialize jackpot feed if jackpot is enabled.
        if (!enabled) {
            return () => {};
        }

        if (tiersCount === 0) {
            setUpdatedTiers(
                tiers?.map((tier: UpdatedJackpotTier, index: number) => ({
                    ...tier,
                    currentAmount: tiers[index]?.min_seed,
                    prevAmount: tiers[index]?.min_seed
                }))
            );
        }

        const providerUid = jackpotData?.provider?.uid;
        if (!providerUid) {
            return () => {};
        }

        const region = getCookieValue(COOKIES.REGION);
        if (!region) {
            return () => {};
        }

        const instance = new JackpotFeed();

        const apiUrl = buildApiUrl(config?.apis?.SiteWideJackpotClient).replace('statePlaceholder', region);
        instance.init({
            host: apiUrl,
            provider: providerUid,
            persistValue: true,
            timeout: 5000,
            jackpotId: providerJackpotId,
            successCallback: handleJackpotUpdate,
            errorCallback: handleJackpotError
        });
    }, [enabled]);

    useEffect(() => {
        if (tiersCount === 0) {
            return () => undefined;
        }

        const updateActiveIndex = () => {
            setActiveIndex((prevActive: number) => (prevActive === tiersCount - 1 ? 0 : prevActive + 1));
        };

        const interval = setInterval(updateActiveIndex, 4000);

        return () => clearInterval(interval);
    }, [tiersCount, setActiveIndex]);
};
